/**
 * WizardContextBar.tsx — Fila 2 de la Cabecera B (solo rutas del wizard P1-P4)
 *
 * Sesión T1CB (Fase 1 · bloque transversal T1). Criterios:
 * `.ptel/CRITERIOS_SHELL_UI_P1_P2_20260702_2240.md` §1 (Cabecera B, dos filas).
 * Referencia visual: `.ptel/P1_NORMALIZA_PROTOTIPO_v2_SHELL_B_20260702.html`.
 *
 * FILAPASOS (25-sep-2026, maquetas C′ + D2 selladas por Luis,
 * `.ptel/FILAPASOS_MAQUETA_v2_20260925.html`): la fila lleva **solo los
 * cuatro pasos**, centrados y con más aire (iconos 14 px, texto 13 px,
 * `padding 6px 14px`, chevrones con margen). La identidad del documento
 * (fichero · municipio (provincia) · INE) y «Cancelar documento» (icono ✕)
 * salen de aquí a `DocumentStrip` (más abajo, mismo fichero): en P1 es la
 * primera línea del panel `DocumentStatusModule` y en P2-P4 una tira de
 * 30 px encima del contenido del paso. El binario «Documento cargado» de
 * §9 desaparece (derogado en CRITERIOS_SHELL_UI).
 *
 * La fila 1 (HeaderSection) vive persistente en App.tsx sobre <Routes> y NO
 * se replica aquí (criterio de coherencia §1).
 */

import type { CSSProperties, FC } from 'react'
import {
  WandSparkles,
  CircleCheck,
  Table,
  Download,
  ChevronRight,
  FileText,
  X,
} from 'lucide-react'
import { colors, typography } from '@/design-system/v2'

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type StepId = 1 | 2 | 3 | 4

export interface WizardDocumentContext {
  fileName: string
  municipality?: string
  province?: string
  ineCode?: string
}

export interface WizardContextBarProps {
  currentStep: StepId
  completedSteps?: StepId[]
  onStepClick?: (step: StepId) => void
}

interface StepConfig {
  id: StepId
  label: string
  Icon: FC<{ size?: number | string; style?: CSSProperties; 'aria-hidden'?: boolean }>
}

const STEPS: StepConfig[] = [
  { id: 1, label: 'Normaliza', Icon: WandSparkles },
  { id: 2, label: 'Valida', Icon: CircleCheck },
  { id: 3, label: 'Coteja', Icon: Table },
  { id: 4, label: 'Exporta', Icon: Download },
]

// ─────────────────────────────────────────────────────────────────────────────
// ESTILOS (DS v2 · sin glow ni box-shadow)
// ─────────────────────────────────────────────────────────────────────────────

const barStyle: CSSProperties = {
  position: 'sticky',
  top: '68px', // altura de la fila 1 (HeaderSection fija)
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  padding: '0 16px',
  background: colors.bg.primary,
  borderTop: `1px solid ${colors.border.light}`,
  borderBottom: `1px solid ${colors.border.light}`,
  fontFamily: typography.fontFamily.sans,
}

const stepperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}

const sepStyle: CSSProperties = {
  color: colors.fg.subtle,
  display: 'flex',
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE — fila de pasos (D2)
// ─────────────────────────────────────────────────────────────────────────────

export const WizardContextBar: FC<WizardContextBarProps> = ({
  currentStep,
  completedSteps = [],
  onStepClick,
}) => {
  return (
    <div style={barStyle} data-testid="wizard-context-bar">
      <nav style={stepperStyle} role="navigation" aria-label="Pasos del asistente PTEL">
        {STEPS.map((step, index) => {
          const isActive = step.id === currentStep
          const isDone = completedSteps.includes(step.id) && !isActive
          const isClickable = !!onStepClick

          const stepStyle: CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            borderRadius: '6px',
            border: 'none',
            background: isActive ? colors.primaryMuted : 'transparent',
            color: isActive
              ? colors.primary
              : isDone
                ? colors.success
                : colors.fg.muted,
            fontSize: '13px',
            fontFamily: typography.fontFamily.sans,
            cursor: isClickable ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
          }

          const { Icon } = step
          return (
            <span key={step.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                style={stepStyle}
                onClick={() => onStepClick?.(step.id)}
                aria-current={isActive ? 'step' : undefined}
                // Contrato ARIA heredado del StepperFloating — los E2E
                // (WP1-T03/T11) dependen del sufijo de estado entre paréntesis.
                aria-label={`Paso ${step.id}: ${step.label}${isDone ? ' (completado)' : isActive ? ' (activo)' : ' (pendiente)'}`}
              >
                <Icon size={14} aria-hidden />
                {step.label}
              </button>
              {index < STEPS.length - 1 && (
                <span style={sepStyle} aria-hidden>
                  <ChevronRight size={14} />
                </span>
              )}
            </span>
          )
        })}
      </nav>
    </div>
  )
}

export default WizardContextBar

// ─────────────────────────────────────────────────────────────────────────────
// DocumentStrip — identidad del documento + «Cancelar documento» (FILAPASOS)
// ─────────────────────────────────────────────────────────────────────────────

export interface DocumentStripProps {
  documento: WizardDocumentContext
  /**
   * `panel`: primera línea del panel de P1 (`DocumentStatusModule`), separada
   *          del resto por una raya tenue (maqueta C′).
   * `strip`: tira de 30 px encima del contenido del paso en P2-P4.
   */
  variant: 'panel' | 'strip'
  /** Cancelar y cargar otro documento; sin él no se pinta el ✕ */
  onCancel?: () => void
}

const stripBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  fontFamily: typography.fontFamily.sans,
  fontSize: '12.5px',
  color: colors.fg.secondary,
  whiteSpace: 'nowrap',
}

const stripVariant: Record<DocumentStripProps['variant'], CSSProperties> = {
  panel: {
    paddingBottom: '8px',
    borderBottom: `1px solid ${colors.border.light}`,
  },
  strip: {
    height: '30px',
    padding: '0 16px',
    background: colors.bg.primary,
    borderBottom: `1px solid ${colors.border.light}`,
  },
}

const stripNameStyle: CSSProperties = {
  color: colors.fg.primary,
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}

const stripMonoStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
  color: colors.fg.muted,
}

const cancelStyle: CSSProperties = {
  width: '24px',
  height: '24px',
  marginLeft: 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: 'transparent',
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '6px',
  color: colors.fg.muted,
  cursor: 'pointer',
  padding: 0,
}

export const DocumentStrip: FC<DocumentStripProps> = ({ documento, variant, onCancel }) => (
  <div
    data-testid="doc-context"
    data-variant={variant}
    style={{ ...stripBase, ...stripVariant[variant] }}
  >
    <FileText size={14} aria-hidden style={{ flexShrink: 0 }} />
    <span style={stripNameStyle}>{documento.fileName}</span>
    {(documento.municipality || documento.ineCode) && (
      <span style={stripMonoStyle}>
        {documento.municipality
          ? ` · ${documento.municipality}${documento.province ? ` (${documento.province})` : ''}`
          : ''}
        {documento.ineCode ? ` · ${documento.ineCode}` : ''}
      </span>
    )}
    {onCancel && (
      <button
        type="button"
        onClick={onCancel}
        aria-label="Cancelar documento"
        title="Cancelar y cargar otro documento"
        style={cancelStyle}
      >
        <X size={14} aria-hidden />
      </button>
    )}
  </div>
)
